import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App as CapApp } from '@capacitor/app';
import { supabase } from '../lib/supabase';

const AuthCtx = createContext(null);

const GOOGLE_DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file';
const GOOGLE_BASE_SCOPES = 'openid email profile';

// Na natívnej (Capacitor) platforme OAuth/magic-link redirectuje na custom scheme
// deep link, ktorý znovu otvorí appku — namiesto https://localhost (čo by ostalo
// v systémovom prehliadači). Na webe ostáva pôvodné správanie.
const IS_NATIVE = Capacitor.isNativePlatform();
const NATIVE_REDIRECT = 'sk.todue.app://auth/callback';
const authRedirect = () =>
  IS_NATIVE ? NATIVE_REDIRECT : window.location.origin + '/auth/callback';

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      // Supabase fíruje onAuthStateChange (SIGNED_IN) pri každom návrate fokusu na
      // tab/okno. Naivné setSession(s) dá novú referenciu session → re-render celej
      // appky a refetch dát (WorkspaceContext.fetch závisí na `user`). Aktualizuj len
      // pri reálnej zmene prihlásenia (token alebo user), inak nechaj pôvodnú referenciu.
      setSession((prev) => {
        if (prev?.access_token === s?.access_token && prev?.user?.id === s?.user?.id) {
          return prev;
        }
        return s;
      });
    });

    // Natívny deep-link návrat z OAuth/magic-link: appka sa znovu otvorí cez
    // sk.todue.app://auth/callback?code=... — dokončíme prihlásenie a zavrieme browser.
    const handleAuthDeepLink = async (url) => {
      if (!url || !url.includes('auth/callback')) return;
      // Drive connect návrat (nie login) — token už uložil callback, len zavrieme okno.
      // O zvyšok (toast, resolve) sa stará polling v requestDriveAccess.
      if (url.includes('drive_connected')) {
        try { await Browser.close(); } catch { /* okno už zatvorené */ }
        return;
      }
      try {
        const parsed = new URL(url);
        const code = parsed.searchParams.get('code');
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) console.error('[deeplink] exchangeCodeForSession:', error.message);
        } else if (url.includes('access_token')) {
          const hp = new URLSearchParams((url.split('#')[1] || ''));
          const at = hp.get('access_token');
          const rt = hp.get('refresh_token');
          if (at && rt) await supabase.auth.setSession({ access_token: at, refresh_token: rt });
        }
      } catch (e) {
        console.error('[deeplink] handling failed:', e);
      } finally {
        try { await Browser.close(); } catch { /* browser už zatvorený */ }
      }
    };

    let urlListener;
    if (IS_NATIVE) {
      // Warm: appka beží, deep link príde ako event.
      urlListener = CapApp.addListener('appUrlOpen', ({ url }) => handleAuthDeepLink(url));
      // Cold start: appku spustil deep link skôr než sa listener stihol zaregistrovať
      // — preto skontrolujeme launch URL aj pri inicializácii.
      CapApp.getLaunchUrl().then((res) => { if (res?.url) handleAuthDeepLink(res.url); }).catch(() => {});
    }

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
      if (urlListener) urlListener.then((l) => l.remove()).catch(() => {});
    };
  }, []);

  const signInWithEmail = useCallback(async (email) => {
    const redirectTo = authRedirect();
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: redirectTo },
    });
    if (error) throw error;
  }, []);

  const signInWithPassword = useCallback(async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  const signUpWithPassword = useCallback(async (email, password) => {
    const redirectTo = authRedirect();
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectTo },
    });
    if (error) throw error;
    return data;
  }, []);

  const resetPassword = useCallback(async (email) => {
    const redirectTo = authRedirect();
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) throw error;
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const redirectTo = authRedirect();
    const options = {
      redirectTo,
      scopes: `${GOOGLE_BASE_SCOPES} ${GOOGLE_DRIVE_SCOPE}`,
      queryParams: { access_type: 'offline', prompt: 'consent' },
    };
    if (IS_NATIVE) {
      // Na mobile otvoríme OAuth v in-app prehliadači a vraciame sa cez deep link.
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { ...options, skipBrowserRedirect: true },
      });
      if (error) throw error;
      if (data?.url) await Browser.open({ url: data.url, presentationStyle: 'popover' });
      return;
    }
    const { error } = await supabase.auth.signInWithOAuth({ provider: 'google', options });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
  }, []);

  // Phase 1 of GDRIVE-MIGRATION-PLAN — separate OAuth flow for Drive scope.
  // Uses DB polling instead of postMessage because Chrome's Cross-Origin-Opener-Policy
  // blocks postMessage from supabase.co popup back to localhost/app.todue.sk opener.
  // Polling user_google_tokens for the row inserted by oauth-drive-callback is robust
  // across COOP and COEP, and handles the race window where popup closes before message dispatches.
  const requestDriveAccess = useCallback(async () => {
    if (!session?.user?.id) throw new Error('Not signed in');
    const userId = session.user.id;

    // Natívna appka (Capacitor): Drive OAuth v in-app Browseri, návrat cez deep link
    // (sk.todue.app://auth/callback?drive_connected=1) — rovnaký princíp ako login.
    // Promise sa resolvne pollovaním has_drive_access(), keď callback uloží token.
    if (IS_NATIVE) {
      const { data, error } = await supabase.functions.invoke('drive-oauth-start', {
        body: { return_url: NATIVE_REDIRECT },
      });
      if (error) throw new Error(`drive-oauth-start failed: ${error.message}`);
      if (!data?.auth_url) throw new Error('drive-oauth-start returned no auth_url');
      await Browser.open({ url: data.auth_url, presentationStyle: 'popover' });
      return new Promise((resolve, reject) => {
        let tries = 0;
        const iv = setInterval(async () => {
          tries += 1;
          try {
            const { data: connected } = await supabase.rpc('has_drive_access');
            if (connected === true) {
              clearInterval(iv);
              try { await Browser.close(); } catch (_) { /* okno už zatvorené */ }
              resolve(true);
              return;
            }
          } catch (_) { /* transient — pokračuj v pollovaní */ }
          if (tries > 120) { clearInterval(iv); reject(new Error('Drive connect timeout')); }
        }, 1500);
      });
    }

    const isMobile = /Mobi|Android/i.test(navigator.userAgent);
    const returnUrl = isMobile ? window.location.href : null;

    const { data, error } = await supabase.functions.invoke('drive-oauth-start', {
      body: returnUrl ? { return_url: returnUrl } : undefined,
    });
    if (error) throw new Error(`drive-oauth-start failed: ${error.message}`);
    if (!data?.auth_url) throw new Error('drive-oauth-start returned no auth_url');

    if (isMobile) {
      sessionStorage.setItem('drive_oauth_return', returnUrl);
      window.location.href = data.auth_url;
      return new Promise(() => { /* navigates away — never resolves */ });
    }

    const popup = window.open(
      data.auth_url,
      'drive-oauth',
      'width=520,height=720,popup=yes',
    );
    if (!popup) throw new Error('Popup blocked. Allow popups for tento web.');

    return new Promise((resolve, reject) => {
      let settled = false;
      const cleanup = () => {
        clearInterval(pollTokenInterval);
        clearInterval(closedPoll);
        clearTimeout(timeoutHandle);
      };
      const finish = (fn, val) => {
        if (settled) return;
        settled = true;
        cleanup();
        fn(val);
      };

      // Poll DB via has_drive_access() RPC. Direct SELECT on user_google_tokens
      // is RLS-blocked for users (refresh_token must not be readable client-side),
      // so we use a SECURITY DEFINER function that returns just a boolean.
      const pollTokenInterval = setInterval(async () => {
        try {
          const { data: connected } = await supabase.rpc('has_drive_access');
          if (connected === true) {
            try { popup.close(); } catch (_) { /* COOP may block, ignore */ }
            finish(resolve, true);
          }
        } catch (_) {
          // Transient errors — keep polling until timeout
        }
      }, 1500);

      // Detect popup close. Wrapped in try/catch because Cross-Origin-Opener-Policy
      // can throw when reading `popup.closed` from a cross-origin window. Grace
      // period of 3s after detected-closed for DB write to be visible.
      let popupClosedAt = null;
      const closedPoll = setInterval(() => {
        if (settled) return;
        let closed = false;
        try { closed = popup.closed; } catch (_) { /* COOP block — assume open */ }
        if (closed) {
          if (!popupClosedAt) popupClosedAt = Date.now();
          else if (Date.now() - popupClosedAt > 3000) {
            finish(reject, new Error('Pripojenie zrušené — popup zatvorený pred dokončením.'));
          }
        }
      }, 500);

      const timeoutHandle = setTimeout(() => {
        try { popup.close(); } catch (_) { /* ignore */ }
        finish(reject, new Error('Drive connect timeout'));
      }, 180_000);
    });
  }, [session]);

  const purgeDriveCredentials = useCallback(async () => {
    const { error } = await supabase.rpc('purge_drive_credentials');
    if (error) throw error;
  }, []);

  const value = {
    session,
    user: session?.user || null,
    loading,
    signInWithEmail,
    signInWithPassword,
    signUpWithPassword,
    resetPassword,
    signInWithGoogle,
    signOut,
    requestDriveAccess,
    purgeDriveCredentials,
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
